import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { client } from '@/services/api/axios.ts';
import { usePermission } from '@/hooks/use-permission.ts';
import { InviteUserPayload } from '@/features/users/schemas/invite-user.schema.ts';

export type InviteUserError = {
  status: number | undefined;
  toastMessage: string;
  emailFieldError?: string;
};

const parseServerMessage = (error: unknown): string | undefined => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    return data?.message;
  }
  return undefined;
};

export const useInviteUserMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: InviteUserError) => void;
} = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canViewUsers = usePermission({ resource: 'users', action: 'view-page.users' });

  return useMutation({
    mutationFn: async (payload: InviteUserPayload) => {
      const { data } = await client.post(`/users/invite`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success(t('users.invite.toasts.success'));
      if (canViewUsers) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const serverMessage = parseServerMessage(error);

      let toastMessage = t('users.invite.toasts.error.generic');
      let emailFieldError: string | undefined;

      if (status === 409) {
        toastMessage = t('users.invite.toasts.error.duplicateEmail');
        emailFieldError = t('users.invite.validation.emailAlreadyExists');
      } else if (status === 422) {
        toastMessage = serverMessage ?? t('users.invite.toasts.error.validation');
      }

      toast.error(toastMessage);

      onError?.({
        status,
        toastMessage,
        emailFieldError,
      });
    },
  });
};
